import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  orendaUserManager,
  hardLogoutOrenda,
  isSigningOut,
  consumeForceReauthFlag,
  continueLogoutChain,
} from "./orendaUserManager";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";
import { getOrendaSessionStatus } from "./orendaAuthStorage";
import orendaService from "../../services/orenda.service";
import { parseApplicationResponse } from "./orendaApplicationHelpers";
import OrendaShell from "./OrendaShell";
import OrendaLegalAgreements from "./OrendaLegalAgreements";
import OrendaKycInternal from "./OrendaKycInternal";
import OrendaSumsub from "./OrendaSumsub";
import OrendaCreateCard from "./OrendaCreateCard";
import OrendaDashboard from "./OrendaDashboard";
import OrendaStepPlaceholder from "./OrendaStepPlaceholder";

// Landing for IssuerOrenda users. Every phase renders inside OrendaShell so
// the layout (branded left panel + progress stepper) is consistent.
//
//   1. Session invalid → auto-signinRedirect to Cognito
//   2. Session valid → fetch viewapplication → dispatch by currentStep

const OrendaLanding = () => {
  const [phase, setPhase] = useState("checking-session");
  const [error, setError] = useState(null);
  const [parsed, setParsed] = useState(null);
  const redirectingRef = useRef(false);

  const fetchApplication = useCallback(async (opts) => {
    // `opts.silent === true` means: skip the loading-state swap; used by
    // background polls that must not remount the current step screen.
    const silent = opts && opts.silent === true;
    // Suppress every outcome if the user has just clicked sign-out — the
    // browser is about to navigate away. Showing an error flash before the
    // redirect lands is pure noise.
    if (isSigningOut()) {
      if (!silent) setPhase("signing-out");
      return;
    }
    if (!silent) {
      setPhase("fetching-app");
      setError(null);
    }
    try {
      const response = await orendaService.viewApplicationOrenda();
      if (isSigningOut()) return;
      setParsed(parseApplicationResponse(response));
      if (!silent) setPhase("ready");
    } catch (err) {
      if (silent) return; // quiet poll: keep the current view
      if (isSigningOut()) return;
      // Translate raw transport errors into something a cardholder can act on.
      const status = err?.response?.status;
      const friendly =
        status === 401 || err?.code === "SSO_SESSION_EXPIRED"
          ? {
              kind: "auth-expired",
              message:
                "Your session has ended. Please sign in again to continue.",
            }
          : status === 403
          ? {
              kind: "forbidden",
              message:
                "We can't load your application right now. If this keeps happening, please contact support.",
            }
          : status >= 500
          ? {
              kind: "server",
              message:
                "We're having trouble reaching the server. Please try again in a moment.",
            }
          : {
              kind: "generic",
              message:
                err?.response?.data?.errorMessage ||
                "Something went wrong loading your application.",
            };
      setError(friendly);
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    // If the user is mid-logout (Entra done, Cognito pending), continue the
    // chain instead of showing anything here. This catches the case where
    // Entra ignores our post_logout_redirect_uri (because the Cognito
    // logout URL isn't whitelisted on the Entra app registration) and
    // leaves the user on Entra's "You signed out" page — from which they
    // come back to our app. Without this they'd see "Signed in. Redirecting…"
    // because OrendaCallback would replay a stale ?code=... from history.
    if (continueLogoutChain()) return;

    const status = getOrendaSessionStatus();
    if (!status.valid) {
      if (redirectingRef.current) return;
      redirectingRef.current = true;
      setPhase("signing-in");
      // If the user just signed out, force every layer of the auth chain
      // to re-authenticate — Cognito's own session, Entra's federated
      // session, and the device-bound SSO on managed Windows machines.
      // max_age=0 forces Entra to re-prompt for credentials because the
      // OIDC spec says id_token.auth_time MUST be >= (now - max_age),
      // which is impossible for a cached session. This is the ONLY
      // signal that reliably overrides Chrome's identity sync and the
      // Windows Primary Refresh Token.
      const forceReauth = consumeForceReauthFlag();
      const extraQueryParams = {
        identity_provider: orendaOidcConfig.identity_provider,
        prompt: "login",
        ...(forceReauth ? { max_age: "0" } : {}),
      };
      orendaUserManager
        .signinRedirect({
          extraQueryParams,
        })
        .catch((err) => {
          setError({ message: err?.message || String(err) });
          setPhase("error");
        });
      return;
    }
    fetchApplication();
  }, [fetchApplication]);

  const currentStep = parsed?.status?.currentStep;

  // Once the user reaches the DashBoard step the UI is a full product
  // dashboard, not an onboarding wizard — bypass OrendaShell entirely so
  // OrendaDashboard owns the whole viewport (its own nav rail + right rail).
  const onDashboard =
    currentStep === "DashBoard" ||
    currentStep === "Dashboard" ||
    currentStep === "DASHBOARD";
  if (phase === "ready" && onDashboard && parsed) {
    return (
      <OrendaDashboard
        application={parsed.application}
        status={parsed.status}
        raw={parsed.raw}
        cachedCardDetails={parsed.cardDetails}
        onAdvance={fetchApplication}
      />
    );
  }

  // Everything else still runs inside the onboarding shell.
  const pagename = ["Welcome to", <br key="br" />, "Bopayments"];
  const pagemsg = "Complete a few quick steps to activate your card.";

  return (
    <OrendaShell
      currentStep={currentStep ?? null}
      pagename={pagename}
      pagemsg={pagemsg}
    >
      {phase === "checking-session" && (
        <div className="orenda-card">Checking your session…</div>
      )}

      {phase === "signing-in" && (
        <>
          <h2 className="orenda-heading">Redirecting you to sign in</h2>
          <p className="orenda-subheading">
            You'll be sent to Orenda's secure sign-in page. If nothing happens,
            use the button below.
          </p>
          <button
            onClick={hardLogoutOrenda}
            className="orenda-btn-primary"
          >
            Sign in with Orenda
          </button>
        </>
      )}

      {phase === "fetching-app" && (
        <div className="orenda-card">Loading your application…</div>
      )}

      {phase === "signing-out" && (
        <div className="orenda-card">Signing you out…</div>
      )}

      {phase === "error" && error?.kind === "auth-expired" && (
        <>
          <h2 className="orenda-heading">Session ended</h2>
          <p className="orenda-subheading">
            For your security, we need you to sign in again.
          </p>
          <div className="orenda-actions">
            <button
              onClick={() => {
                const forceReauth = consumeForceReauthFlag();
                orendaUserManager
                  .signinRedirect({
                    extraQueryParams: {
                      identity_provider: orendaOidcConfig.identity_provider,
                      prompt: "login",
                      ...(forceReauth ? { max_age: "0" } : {}),
                    },
                  })
                  .catch(() => {});
              }}
              className="orenda-btn-primary"
            >
              Sign in again
            </button>
          </div>
        </>
      )}

      {phase === "error" && error?.kind !== "auth-expired" && (
        <>
          <h2 className="orenda-heading">
            {error?.kind === "server"
              ? "We're having trouble connecting"
              : "Something went wrong"}
          </h2>
          <p className="orenda-subheading">
            {error?.message || "An unexpected error occurred."}
          </p>
          <div className="orenda-actions">
            <button onClick={fetchApplication} className="orenda-btn-primary">
              Try again
            </button>
          </div>
        </>
      )}

      {phase === "ready" && parsed && (
        <StepRouter parsed={parsed} onAdvance={fetchApplication} />
      )}
    </OrendaShell>
  );
};

// Orenda v2 onboarding step router.
// Docs: https://api-docs.orenda-apps.com/onboarding/overview/
//
// Action-required steps map to a dedicated screen; waiting/terminal steps map
// to the placeholder which polls and shows the right messaging. Legacy v1
// step names (KYC_INTERNAL, KYC_SUMSUB, RISK, HOLDER_CREATION, ...) are kept
// as aliases so we don't break if the backend returns either during the
// transition.
const StepRouter = ({ parsed, onAdvance }) => {
  const step = parsed.status?.currentStep;
  switch (step) {
    case "LEGAL_AGREEMENTS":
      return (
        <OrendaLegalAgreements
          application={parsed.application}
          status={parsed.status}
          raw={parsed.raw}
          onAdvance={onAdvance}
        />
      );
    case "KYC_FORM":
    case "KYC_INTERNAL":                     // v1 alias
    case "KYC_INTERNAL_VALIDATION_ERRORS":   // v1 alias (retry)
      return (
        <OrendaKycInternal
          application={parsed.application}
          status={parsed.status}
          raw={parsed.raw}
          onAdvance={onAdvance}
        />
      );
    case "IDENTITY_VERIFICATION":
    case "KYC_SUMSUB":                       // v1 alias
      return (
        <OrendaSumsub
          application={parsed.application}
          status={parsed.status}
          raw={parsed.raw}
          onAdvance={onAdvance}
        />
      );
    // COMPLETED is the trigger for the final onboarding action: issue the
    // applicant's first card. OrendaCreateCard reads sessionStorage so a
    // user who already created a card and refreshes will skip the CTA.
    case "COMPLETED":
      return (
        <OrendaCreateCard
          application={parsed.application}
          status={parsed.status}
          raw={parsed.raw}
          onAdvance={onAdvance}
        />
      );
    // DashBoard — set by the backend once the card is issued. User lands
    // here on every subsequent visit to the Orenda app.
    case "DashBoard":
    case "Dashboard":
    case "DASHBOARD":
      return (
        <OrendaDashboard
          application={parsed.application}
          status={parsed.status}
          raw={parsed.raw}
          cachedCardDetails={parsed.cardDetails}
          onAdvance={onAdvance}
        />
      );
    // Waiting and terminal states — the placeholder decides what to render
    // based on currentStep and whether to keep polling.
    case "PROCESSING":
    case "IN_REVIEW":
    case "DECLINED":
    case "ERROR":
    case "ADD_UBOS":                         // company-only; render placeholder for now
    case "PROVIDER_DOCUMENTS":               // rare; placeholder with support contact
    // v1 aliases that collapse into v2 waiting / terminal states:
    case "KYC":
    case "RISK":
    case "HOLDER_CREATION":
    case "ACCOUNT_CREATION":
    default:
      return (
        <OrendaStepPlaceholder
          application={parsed.application}
          status={parsed.status}
          onAdvance={onAdvance}
        />
      );
  }
};

export default OrendaLanding;
